import {
  UPLOAD_DB_NAME,
  UPLOAD_DB_VERSION,
  UPLOAD_INDEXES,
  UPLOAD_STORES
} from "./uploadPersistenceSchema";

const STORE_DEFINITIONS = [
  { name: UPLOAD_STORES.jobs, options: { keyPath: "id" } },
  { name: UPLOAD_STORES.sources, options: { keyPath: "id" } },
  { name: UPLOAD_STORES.schemaMeta, options: { keyPath: "key" } }
];

const TERMINAL_JOB_STATUSES = new Set(["complete", "cancelled"]);

function hasName(names, name) {
  return Boolean(
    names &&
      (typeof names.contains === "function"
        ? names.contains(name)
        : Array.prototype.includes.call(names, name))
  );
}

function ensureIndexes(store) {
  (UPLOAD_INDEXES[store.name] || []).forEach(index => {
    if (!hasName(store.indexNames, index.name)) {
      store.createIndex(index.name, index.keyPath, index.options || {});
    }
  });
}

function ensureSchema(db, transaction) {
  STORE_DEFINITIONS.forEach(definition => {
    const store = hasName(db.objectStoreNames, definition.name)
      ? transaction.objectStore(definition.name)
      : db.createObjectStore(definition.name, definition.options);

    ensureIndexes(store);
  });
}

function assertDatabase(db) {
  if (!db || typeof db.transaction !== "function") {
    throw new Error(
      "A valid IndexedDB database is required for upload persistence."
    );
  }
}

function waitForRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = event => resolve(event.target.result);
    request.onerror = () =>
      reject(request.error || new Error("Upload persistence request failed."));
  });
}

function waitForTransaction(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(
        transaction.error || new Error("Upload persistence transaction failed.")
      );
    transaction.onabort = () =>
      reject(
        transaction.error ||
          new Error("Upload persistence transaction aborted.")
      );
  });
}

function getAllFromStore(store) {
  if (typeof store.getAll === "function") {
    return waitForRequest(store.getAll());
  }

  return new Promise((resolve, reject) => {
    const records = [];
    const request = store.openCursor();

    request.onsuccess = event => {
      const cursor = event.target.result;
      if (!cursor) {
        resolve(records);
        return;
      }
      records.push(cursor.value);
      cursor.continue();
    };
    request.onerror = () =>
      reject(request.error || new Error("Unable to read upload records."));
  });
}

function getAllByJobId(store, jobId) {
  if (typeof store.index === "function") {
    const index = store.index("jobId");
    if (index && typeof index.getAll === "function") {
      return waitForRequest(index.getAll(jobId));
    }
  }

  return getAllFromStore(store).then(records =>
    records.filter(record => record.jobId === jobId)
  );
}

function sortByUpdatedAtDescending(jobs) {
  return jobs.sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt || 0).getTime() -
      new Date(a.updatedAt || a.createdAt || 0).getTime()
  );
}

function validateSources(jobId, sources) {
  if (!Array.isArray(sources) || sources.length === 0) {
    throw new Error("An upload job must include at least one source file.");
  }

  const ids = new Set();
  sources.forEach(source => {
    if (!source || !source.id || !source.blob) {
      throw new Error("Upload sources must include an id and blob.");
    }
    if (source.jobId !== jobId) {
      throw new Error("Upload source jobId must match its upload job.");
    }
    if (ids.has(source.id)) {
      throw new Error(`Duplicate upload source id: ${source.id}`);
    }
    ids.add(source.id);
  });
}

export function openUploadPersistence({ indexedDBImpl } = {}) {
  const indexedDBApi =
    indexedDBImpl || (typeof indexedDB !== "undefined" ? indexedDB : null);

  if (!indexedDBApi || typeof indexedDBApi.open !== "function") {
    return Promise.reject(
      new Error(
        "IndexedDB is unavailable; uploads cannot persist across refresh."
      )
    );
  }

  return new Promise((resolve, reject) => {
    const request = indexedDBApi.open(UPLOAD_DB_NAME, UPLOAD_DB_VERSION);

    request.onupgradeneeded = event => {
      ensureSchema(event.target.result, event.target.transaction);
    };
    request.onsuccess = event => resolve(event.target.result);
    request.onerror = () =>
      reject(
        request.error ||
          new Error("Unable to open upload persistence database.")
      );
    request.onblocked = () =>
      reject(
        new Error(
          "Upload persistence upgrade is blocked by another browser tab."
        )
      );
  });
}

export async function requestPersistentUploadStorage({ storageManager } = {}) {
  const manager =
    storageManager ||
    (typeof navigator !== "undefined" ? navigator.storage : null);

  if (!manager || typeof manager.persist !== "function") {
    return false;
  }

  try {
    return Boolean(await manager.persist());
  } catch (error) {
    return false;
  }
}

export async function createUploadJob({ db, job, sources }) {
  assertDatabase(db);

  if (!job || !job.id) {
    throw new Error("Upload job must include an id.");
  }
  validateSources(job.id, sources);

  const transaction = db.transaction(
    [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
    "readwrite"
  );
  const jobStore = transaction.objectStore(UPLOAD_STORES.jobs);
  const sourceStore = transaction.objectStore(UPLOAD_STORES.sources);
  const complete = waitForTransaction(transaction);

  jobStore.add(job);
  sources.forEach(source => sourceStore.add(source));
  await complete;

  return { jobId: job.id, sourceCount: sources.length };
}

export async function getUploadJob({ db, jobId }) {
  assertDatabase(db);

  const transaction = db.transaction(
    [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
    "readonly"
  );
  const [job, sources] = await Promise.all([
    waitForRequest(transaction.objectStore(UPLOAD_STORES.jobs).get(jobId)),
    getAllByJobId(transaction.objectStore(UPLOAD_STORES.sources), jobId)
  ]);

  return job ? { job, sources } : null;
}

export async function listIncompleteUploadJobs({ db }) {
  assertDatabase(db);
  const transaction = db.transaction([UPLOAD_STORES.jobs], "readonly");
  const jobs = await getAllFromStore(
    transaction.objectStore(UPLOAD_STORES.jobs)
  );

  return sortByUpdatedAtDescending(
    jobs.filter(job => !TERMINAL_JOB_STATUSES.has(job.status))
  );
}

export async function normalizeIncompleteUploadJobs({ db }) {
  assertDatabase(db);
  const readTransaction = db.transaction(
    [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
    "readonly"
  );
  const [jobs, sources] = await Promise.all([
    getAllFromStore(readTransaction.objectStore(UPLOAD_STORES.jobs)),
    getAllFromStore(readTransaction.objectStore(UPLOAD_STORES.sources))
  ]);
  const updatedAt = new Date().toISOString();
  const jobsToUpdate = jobs
    .filter(job => ["queued", "running", "uploaded"].includes(job.status))
    .map(job => ({ ...job, status: "paused", updatedAt }));
  const sourcesToUpdate = sources
    .filter(source => source.status === "running")
    .map(source => ({ ...source, status: "pending", updatedAt }));

  if (jobsToUpdate.length || sourcesToUpdate.length) {
    const transaction = db.transaction(
      [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
      "readwrite"
    );
    const complete = waitForTransaction(transaction);
    const jobStore = transaction.objectStore(UPLOAD_STORES.jobs);
    const sourceStore = transaction.objectStore(UPLOAD_STORES.sources);
    jobsToUpdate.forEach(job => jobStore.put(job));
    sourcesToUpdate.forEach(source => sourceStore.put(source));
    await complete;
  }

  return {
    jobsUpdated: jobsToUpdate.length,
    sourcesUpdated: sourcesToUpdate.length
  };
}

export async function updateUploadJob({ db, job }) {
  assertDatabase(db);
  if (!job || !job.id) {
    throw new Error("Upload job record must include an id.");
  }
  const transaction = db.transaction([UPLOAD_STORES.jobs], "readwrite");
  const complete = waitForTransaction(transaction);
  transaction.objectStore(UPLOAD_STORES.jobs).put(job);
  await complete;
  return job;
}

export async function updateUploadSource({ db, source }) {
  assertDatabase(db);
  if (!source || !source.id) {
    throw new Error("Upload source record must include an id.");
  }
  const transaction = db.transaction([UPLOAD_STORES.sources], "readwrite");
  const complete = waitForTransaction(transaction);
  transaction.objectStore(UPLOAD_STORES.sources).put(source);
  await complete;
  return source;
}

export async function deleteUploadSources({ db, jobId }) {
  assertDatabase(db);
  if (!jobId) {
    throw new Error("An upload job id is required.");
  }

  const readTransaction = db.transaction([UPLOAD_STORES.sources], "readonly");
  const sources = await getAllByJobId(
    readTransaction.objectStore(UPLOAD_STORES.sources),
    jobId
  );

  if (sources.length) {
    const transaction = db.transaction([UPLOAD_STORES.sources], "readwrite");
    const complete = waitForTransaction(transaction);
    const sourceStore = transaction.objectStore(UPLOAD_STORES.sources);
    sources.forEach(source => sourceStore.delete(source.id));
    await complete;
  }

  return sources.length;
}

export async function cancelUploadJob({ db, jobId }) {
  assertDatabase(db);
  const record = await getUploadJob({ db, jobId });
  if (!record) return null;
  if (record.job.status === "complete") return record.job;
  if (record.job.status === "cancelled") {
    await deleteUploadSources({ db, jobId });
    return record.job;
  }

  const cancelledAt = new Date().toISOString();
  const cancelledJob = {
    ...record.job,
    status: "cancelled",
    cancelledAt,
    updatedAt: cancelledAt
  };
  const transaction = db.transaction(
    [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
    "readwrite"
  );
  const complete = waitForTransaction(transaction);
  transaction.objectStore(UPLOAD_STORES.jobs).put(cancelledJob);
  const sourceStore = transaction.objectStore(UPLOAD_STORES.sources);
  record.sources.forEach(source => sourceStore.delete(source.id));
  await complete;

  return cancelledJob;
}

export async function completeUploadJob({ db, jobId }) {
  assertDatabase(db);
  const record = await getUploadJob({ db, jobId });
  if (!record) return null;

  const completedAt = record.job.completedAt || new Date().toISOString();
  const completedJob = {
    ...record.job,
    status: "complete",
    completedAt,
    completedBytes: record.job.totalBytes,
    updatedAt: completedAt
  };
  const transaction = db.transaction(
    [UPLOAD_STORES.jobs, UPLOAD_STORES.sources],
    "readwrite"
  );
  const complete = waitForTransaction(transaction);
  transaction.objectStore(UPLOAD_STORES.jobs).put(completedJob);
  const sourceStore = transaction.objectStore(UPLOAD_STORES.sources);
  record.sources.forEach(source => sourceStore.delete(source.id));
  await complete;

  return completedJob;
}
